import { useCallback } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { tooltipMaxWidth } from '@cbhq/cds-common/tokens/tooltip';
import { IconButton } from '@cbhq/cds-web/buttons/IconButton';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { Tooltip } from '@cbhq/cds-web/overlays/tooltip/Tooltip';
import { Text } from '@cbhq/cds-web/typography/Text';

import { useMinuteTick } from '../hooks/useMinuteTick';
import { formatEffectiveAt, isPending } from '../utils/gracePeriodFormat';

const messages = defineMessages({
  starts: {
    id: 'Team.pendingRoleInfo.starts',
    defaultMessage: 'Newly granted {roleName} access starts {startsAt}.',
    description:
      'Team table tooltip for a role (e.g. Admin) that is granted but still waiting out its delay. {roleName} is the role name, e.g. "Admin". {startsAt} is the start date and time, e.g. "Sep 27, 10:00 AM PDT".',
  },
  startsOnEntity: {
    id: 'Team.pendingRoleInfo.startsOnEntity',
    defaultMessage: 'Newly granted {roleName} access for {entityName} starts {startsAt}.',
    description:
      'Team table tooltip for a role that is granted on one legal entity but still waiting out its delay. {roleName} is the role name, e.g. "Admin". {entityName} is the legal entity, e.g. "Acme US". {startsAt} is the start date and time, e.g. "Sep 27, 10:00 AM PDT".',
  },
  startsOnSeveralEntities: {
    id: 'Team.pendingRoleInfo.startsOnSeveralEntities',
    defaultMessage: 'Newly granted {roleName} access starts at these times:',
    description:
      'Team table tooltip lead line for a role granted on several legal entities that is still waiting out its delay. One line per entity follows. {roleName} is the role name, e.g. "Admin".',
  },
  entityStarts: {
    id: 'Team.pendingRoleInfo.entityStarts',
    defaultMessage: '{entityName}: starts {startsAt}',
    description:
      'Team table tooltip line, one per legal entity, under "Newly granted Admin access starts at these times:". {entityName} is the legal entity, e.g. "Acme US". {startsAt} is the start date and time, e.g. "Sep 27, 10:00 AM PDT".',
  },
  memberUntilThen: {
    id: 'Team.pendingRoleInfo.memberUntilThen',
    defaultMessage: 'Viewer access applies until then. All admins will be notified.',
    description:
      'Team table tooltip sentences for an existing member with a newly granted role that has not started yet. The member has view-only access until it starts, and every workspace admin gets an email about the change.',
  },
  viewerAccessAfterAcceptUntilThen: {
    id: 'Team.pendingRoleInfo.viewerAccessAfterAcceptUntilThen',
    defaultMessage: 'Once they accept the invite, Viewer access applies until then.',
    description:
      'Team table tooltip sentence for a pending invite with a role (e.g. Admin) that has not started yet. The invitee has no access before accepting, then view-only access until the role starts.',
  },
  accessibilityLabel: {
    id: 'Team.pendingRoleInfo.accessibilityLabel',
    defaultMessage: '{roleName} access pending. Show details',
    description:
      'Accessibility label of the info icon next to a role (e.g. Admin) that is still waiting out its delay. {roleName} is the role name.',
  },
});

const UNTIL_THEN_MESSAGES = {
  pendingRole: messages.memberUntilThen,
  pendingInvite: messages.viewerAccessAfterAcceptUntilThen,
} as const;

export type PendingRoleInfoVariant = 'pendingRole' | 'pendingInvite';

export type PendingRoleInfoEntry = {
  /** Legal entity of the pending grant, e.g. "Acme US". Omit when the member has one entity. */
  entityName?: string;
  /** When the role becomes usable. */
  effectiveAt: Date;
};

export type PendingRoleInfoProps = {
  /** `pendingRole` for members, `pendingInvite` for invites not yet accepted. */
  variant: PendingRoleInfoVariant;
  /** Display name of the requested role, e.g. "Admin". */
  roleName: string;
  /** One entry per pending grant (e.g. per legal entity). Past entries are ignored. */
  entries: PendingRoleInfoEntry[];
  /** Current time. Defaults to now; pass a fixed value in tests and stories. */
  now?: Date;
  testID?: string;
};

/**
 * Info icon with a tooltip explaining that a protected role (e.g. Admin) has
 * been requested but is still in its grace period. Renders nothing once every
 * entry has taken effect.
 */
export function PendingRoleInfo({
  variant,
  roleName,
  entries,
  now,
  testID = 'pending-role-info',
}: PendingRoleInfoProps) {
  const intl = useIntl();

  // Derived on every render (not memoized) so the pending check stays current
  // when `now` is omitted.
  const currentTime = now ?? new Date();
  const pendingEntries = entries
    .filter((entry) => isPending(entry.effectiveAt, currentTime))
    .sort((a, b) => a.effectiveAt.getTime() - b.effectiveAt.getTime())
    .map((entry) => ({
      key: `${entry.entityName ?? ''}-${entry.effectiveAt.toISOString()}`,
      entityName: entry.entityName,
      startsAt: formatEffectiveAt(intl, entry.effectiveAt),
    }));

  // The icon sits inside clickable table rows; keep clicks on it from
  // triggering the row's navigation.
  const handleClick = useCallback((event: MouseEvent) => {
    event.stopPropagation();
  }, []);

  // Re-render every minute while pending so the icon disappears once every
  // entry takes effect.
  useMinuteTick(now == null && pendingEntries.length > 0);

  if (pendingEntries.length === 0) {
    return null;
  }

  const untilThen = intl.formatMessage(UNTIL_THEN_MESSAGES[variant]);
  const [firstEntry] = pendingEntries;

  let content: ReactNode;
  if (pendingEntries.length === 1) {
    const lead =
      firstEntry.entityName != null
        ? intl.formatMessage(messages.startsOnEntity, {
            roleName,
            entityName: firstEntry.entityName,
            startsAt: firstEntry.startsAt,
          })
        : intl.formatMessage(messages.starts, { roleName, startsAt: firstEntry.startsAt });
    content = (
      <Text as="p" font="label2" testID={`${testID}-content`}>
        {lead} {untilThen}
      </Text>
    );
  } else {
    content = (
      <VStack gap={1} testID={`${testID}-content`}>
        <Text as="p" font="label2">
          {intl.formatMessage(messages.startsOnSeveralEntities, { roleName })}
        </Text>
        {pendingEntries.map((entry) => (
          <Text as="p" font="label2" key={entry.key}>
            {intl.formatMessage(messages.entityStarts, {
              entityName: entry.entityName ?? roleName,
              startsAt: entry.startsAt,
            })}
          </Text>
        ))}
        <Text as="p" font="label2">
          {untilThen}
        </Text>
      </VStack>
    );
  }

  return (
    // CDS only applies its default max width to plain-text content.
    <Tooltip content={content} maxWidth={tooltipMaxWidth}>
      <IconButton
        transparent
        compact
        color="fgMuted"
        name="info"
        onClick={handleClick}
        accessibilityLabel={intl.formatMessage(messages.accessibilityLabel, { roleName })}
        testID={testID}
      />
    </Tooltip>
  );
}
